<script lang="ts">
	// D16, the restaurant in Bosla's catalogue (UC-29, UC-32, UC-33), as the
	// Owner Dashboard - Menu page draws it. A status card says whether the restaurant
	// is published and, until it can be, what is missing and why. Below it,
	// one part at a time: the page (whether it is published, its address,
	// name and area, and the logo), or the shops on the map.
	//
	// The server decides everything shown here: which shops the catalogue
	// shows, whether the restaurant can be published, the page's link and the
	// address it follows. A removed logo or place leaves at once with a
	// ten-second Undo, and the server hears of it when the Undo runs out.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Field from '../components/Field.svelte';
	import ImageInput, { type Picked } from '../components/ImageInput.svelte';
	import Loading from '../components/Loading.svelte';
	import MapPin from '../components/MapPin.svelte';
	import Segmented from '../components/Segmented.svelte';
	import { api } from '../api/client';
	import { formatDegrees, readDegrees } from '../format/degrees';
	import { fill, message, type Language, type MessageKey } from '../i18n/messages';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import type { components } from '../api/types';

	type Listing = components['schemas']['ListingBody'];
	type Shop = components['schemas']['ListingShopBody'];

	interface Props {
		language: Language;
		/** Opens a shop in Shops, where its address is changed. */
		onShopAddress: (shopId: string) => void;
		shopId?: string;
	}

	let { language, onShopAddress, shopId = '' }: Props = $props();

	let saved = $state<Listing | null>(null);
	let failure = $state('');
	let part = $state<'page' | 'shops'>('page');

	// The page form.
	let listed = $state(false);
	let slug = $state('');
	let name = $state('');
	let area = $state('');
	let saving = $state(false);
	// The field the server refused, with the words to say under it.
	let refused = $state<{ field: string; key: MessageKey } | null>(null);
	// What publishing still lacked when the server refused it.
	let incomplete = $state<string[]>([]);

	// The logo.
	let uploading = $state(false);
	let logoProblem = $state<'type' | 'size' | 'small' | null>(null);
	// Removed, with its Undo still counting.
	let logoGone = $state(false);

	// The one shop whose place is being edited, as typed.
	let edit = $state<{ id: string; lat: string; lng: string; touched: boolean } | null>(null);
	let placing = $state(false);
	let placeRefused = $state<'latitude' | 'longitude' | null>(null);
	// Places removed, with their Undo still counting.
	let placesGone = $state<string[]>([]);

	function show(l: Listing) {
		saved = l;
		listed = l.listed;
		slug = l.slug;
		name = l.name;
		area = l.area;
	}

	async function load() {
		try {
			const { data, error } = await api.GET('/api/owner/listing');
			if (error !== undefined || !data) {
				failure = error?.message ?? message(language, 'loadFailed');
				return;
			}
			show(data);
		} catch {
			failure = message(language, 'loadFailed');
		}
	}

	$effect(() => {
		void load();
	});

	const logoUrl = $derived(logoGone ? '' : (saved?.logoUrl ?? ''));
	const shops = $derived(
		(saved?.shops ?? []).map((s) =>
			placesGone.includes(s.id) ? { ...s, place: undefined, shown: false } : s,
		),
	);
	const shownCount = $derived(shops.filter((s) => s.shown).length);
	const ready = $derived(logoUrl !== '' && shownCount > 0);
	const changed = $derived(
		saved !== null &&
			(listed !== saved.listed ||
				slug !== saved.slug ||
				name.trim() !== saved.name ||
				area.trim() !== saved.area),
	);
	const nameMissing = $derived(name.trim() === '');
	const shownUrl = $derived(saved?.url.replace(/^https?:\/\//, '') ?? '');

	const fieldKeys: Record<string, MessageKey> = {
		slug: 'listingSlugInvalid',
		name: 'listingNameInvalid',
		area: 'listingAreaInvalid',
	};

	const missingWords: Record<string, MessageKey> = {
		logo: 'catalogueIncompleteLogo',
		shop: 'catalogueIncompleteShop',
	};

	async function save() {
		if (saving) return;
		saving = true;
		failure = '';
		refused = null;
		incomplete = [];
		try {
			const { data, error } = await api.PUT('/api/owner/listing', {
				body: { listed, slug: slug.trim(), name: name.trim(), area: area.trim() },
			});
			if (error !== undefined || !data) {
				const field = String(error?.details?.field ?? '');
				if (error?.code === 'SLUG_TAKEN') {
					refused = { field: 'slug', key: 'listingSlugTaken' };
				} else if (error?.code === 'VALIDATION_ERROR' && fieldKeys[field]) {
					refused = { field, key: fieldKeys[field] };
				} else if (error?.code === 'LISTING_INCOMPLETE') {
					const missing = error.details?.missing;
					incomplete = Array.isArray(missing) ? missing.map(String) : [];
				} else {
					failure = error?.message ?? message(language, 'loadFailed');
				}
				return;
			}
			show(data);
			flash(message(language, 'saved'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			saving = false;
		}
	}

	function errorFor(field: string): string | undefined {
		if (refused?.field === field) return message(language, refused.key);
		if (field === 'name' && nameMissing) return message(language, 'listingNameRequired');
		return undefined;
	}

	function edited(change: () => void) {
		change();
		refused = null;
		incomplete = [];
	}

	const logoWords: Record<'type' | 'size' | 'small', MessageKey> = {
		type: 'logoType',
		size: 'logoSize',
		small: 'logoSmall',
	};

	async function pickLogo(picked: Picked) {
		if (picked.url) URL.revokeObjectURL(picked.url);
		logoProblem = picked.problem;
		if (picked.problem) return;
		uploading = true;
		failure = '';
		try {
			const { data, error } = await api.PUT('/api/owner/listing/logo', {
				// The body is the file itself, sent as it is; the generated
				// type calls a binary body a string.
				body: picked.file as unknown as string,
				bodySerializer: (file) => file,
				headers: { 'Content-Type': 'application/octet-stream' },
			});
			if (error !== undefined || !data) {
				const reason = String(error?.details?.reason ?? '');
				if (error?.code === 'IMAGE_INVALID' && reason in logoWords) {
					logoProblem = reason as 'type' | 'size' | 'small';
				} else {
					failure = error?.message ?? message(language, 'loadFailed');
				}
				return;
			}
			logoGone = false;
			show(data);
			flash(message(language, 'saved'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			uploading = false;
		}
	}

	function removeLogo() {
		logoProblem = null;
		logoGone = true;
		removeWithUndo(
			message(language, 'logoRemoved'),
			async () => {
				try {
					await api.DELETE('/api/owner/listing/logo');
				} finally {
					logoGone = false;
					await load();
				}
			},
			() => (logoGone = false),
		);
	}

	function startEdit(shop: Shop) {
		placeRefused = null;
		edit = {
			id: shop.id,
			lat: shop.place ? formatDegrees(shop.place.latitudeE6) : '',
			lng: shop.place ? formatDegrees(shop.place.longitudeE6) : '',
			touched: false,
		};
	}

	const lat = $derived(edit ? readDegrees(edit.lat, 90) : null);
	const lng = $derived(edit ? readDegrees(edit.lng, 180) : null);
	const both = $derived(
		lat?.e6 !== undefined && lng?.e6 !== undefined ? { lat: lat.e6, lng: lng.e6 } : null,
	);

	function degreesError(
		read: ReturnType<typeof readDegrees> | null,
		range: MessageKey,
		field: 'latitude' | 'longitude',
	): string | undefined {
		if (placeRefused === field) return message(language, range);
		if (!read || !read.problem) return undefined;
		if (read.problem === 'form') return message(language, 'placeDegrees');
		if (read.problem === 'range') return message(language, range);
		return edit?.touched ? message(language, 'placeRequired') : undefined;
	}

	async function savePlace(shop: Shop) {
		if (!both || placing) return;
		placing = true;
		failure = '';
		placeRefused = null;
		try {
			const { data, error } = await api.PUT('/api/owner/shops/{shopId}/location', {
				params: { path: { shopId: shop.id } },
				body: { latitudeE6: both.lat, longitudeE6: both.lng },
			});
			if (error !== undefined || !data) {
				const field = String(error?.details?.field ?? '');
				if (error?.code === 'VALIDATION_ERROR' && (field === 'latitude' || field === 'longitude')) {
					placeRefused = field;
				} else {
					failure = error?.message ?? message(language, 'loadFailed');
				}
				return;
			}
			edit = null;
			show(data);
			flash(message(language, 'saved'));
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			placing = false;
		}
	}

	function removePlace(shop: Shop) {
		edit = null;
		placesGone = [...placesGone, shop.id];
		removeWithUndo(
			fill(message(language, 'placeRemoved'), shop.name),
			async () => {
				try {
					await api.DELETE('/api/owner/shops/{shopId}/location', {
						params: { path: { shopId: shop.id } },
					});
				} finally {
					placesGone = placesGone.filter((id) => id !== shop.id);
					await load();
				}
			},
			() => (placesGone = placesGone.filter((id) => id !== shop.id)),
		);
	}

	function placeText(place: { latitudeE6: number; longitudeE6: number }): string {
		return `${formatDegrees(place.latitudeE6)}, ${formatDegrees(place.longitudeE6)}`;
	}

	function lacks(shop: Shop): string {
		const what = [
			shop.address.trim() === '' && message(language, 'placeLacksAddress'),
			!shop.place && message(language, 'placeLacksPlace'),
		].filter(Boolean);
		return fill(message(language, 'placeLacks'), what.join(message(language, 'placeAnd')));
	}
</script>

<div class="catalogue" data-testid="panel-catalogue">
	{#if failure}
		<Band tone="flag" testId="catalogue-error">{failure}</Band>
	{/if}

	{#if saved === null}
		{#if !failure}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'catalogueLoading')}
					caption={message(language, 'catalogueLoading')}
				/>
			</div>
		{/if}
	{:else}
		{#if !shopId}
			<Card pad testId="catalogue-status">
				<div class="status">
					{#if saved.listed}
						<div class="line">
							<Chip
								tone="ok"
								dot
								size="md"
								text={message(language, 'cataloguePublished')}
								testId="catalogue-published"
							/>
							<bdi class="url" dir="ltr" data-testid="catalogue-url">{shownUrl}</bdi>
							<span class="push"></span>
							{#if saved.url}
								<Button
									testId="catalogue-open"
									tone="secondary"
									size="sm"
									label={message(language, 'catalogueOpen')}
									trailingIcon="external"
									href={saved.url}
									newTab
								/>
							{/if}
						</div>
					{:else}
						<div class="line">
							<Chip
								tone="neutral"
								dot
								size="md"
								text={message(language, 'catalogueNotPublished')}
								testId="catalogue-unpublished"
							/>
							<span class="say">{message(language, 'catalogueNotPublishedNote')}</span>
						</div>
					{/if}
					{#if !ready}
						<div class="missing" data-testid="catalogue-missing">
							<span class="missing-title">{message(language, 'catalogueMissingTitle')}</span>
							{#if logoUrl === ''}
								<div class="need">
									<Chip tone="flag" dot text={message(language, 'catalogueMissingLogo')} />
									<span class="why">{message(language, 'catalogueMissingLogoWhy')}</span>
									<Button
										testId="catalogue-need-logo"
										tone="text"
										size="sm"
										label={message(language, 'logoAdd')}
										onPress={() => (part = 'page')}
									/>
								</div>
							{/if}
							{#if shownCount === 0}
								<div class="need">
									<Chip tone="flag" dot text={message(language, 'catalogueMissingShop')} />
									<span class="why">{message(language, 'catalogueMissingShopWhy')}</span>
									<Button
										testId="catalogue-need-shop"
										tone="text"
										size="sm"
										label={message(language, 'catalogueMissingShopAct')}
										onPress={() => (part = 'shops')}
									/>
								</div>
							{/if}
						</div>
					{/if}
				</div>
			</Card>

			{#if incomplete.length > 0}
				<Band tone="flag" testId="catalogue-incomplete">
					{fill(
						message(language, 'catalogueIncomplete'),
						incomplete
							.map((m) => (missingWords[m] ? message(language, missingWords[m]) : m))
							.join(message(language, 'catalogueAnd')),
					)}
				</Band>
			{/if}

			<div class="parts">
				<Segmented
					label={message(language, 'cataloguePart')}
					options={[
						{
							value: 'page',
							label: message(language, 'cataloguePartPage'),
							testId: 'catalogue-part-page',
						},
						{
							value: 'shops',
							label: message(language, 'cataloguePartShops'),
							testId: 'catalogue-part-shops',
						},
					]}
					value={part}
					onChange={(picked) => (part = picked === 'shops' ? 'shops' : 'page')}
				/>
			</div>
		{/if}

		{#if !shopId && part === 'page'}
			<Card
				title={message(language, 'cataloguePartPage')}
				note={saved.pageBase.replace(/\/$/, '')}
				testId="catalogue-page"
			>
				<div class="body">
					<div class="group">
						<span class="label">{message(language, 'catalogueInIt')}</span>
						<div class="segmented">
							<Segmented
								label={message(language, 'catalogueInIt')}
								options={[
									{
										value: 'listed',
										label: message(language, 'catalogueListed'),
										testId: 'catalogue-listed',
										disabled: !ready && !listed,
									},
									{
										value: 'hidden',
										label: message(language, 'catalogueNotPublished'),
										testId: 'catalogue-hidden',
									},
								]}
								value={listed ? 'listed' : 'hidden'}
								onChange={(picked) => edited(() => (listed = picked === 'listed'))}
							/>
						</div>
						{#if saved.listed && !listed}
							<span class="explain">{message(language, 'catalogueUnpublishNote')}</span>
						{:else if !ready && !listed}
							<span class="explain" data-testid="catalogue-locked">
								{message(language, 'catalogueLockedNote')}
							</span>
						{/if}
					</div>

					<div class="grid">
						<Field
							testId="catalogue-slug"
							label={message(language, 'listingSlug')}
							note={message(language, 'listingSlugNote')}
							error={errorFor('slug')}
							errorTestId="catalogue-slug-error"
							prefix={saved.pageBase || undefined}
							value={slug}
							maxLength={40}
							mono
							onInput={(v) => edited(() => (slug = v.toLowerCase().replace(/\s/g, '')))}
						/>
						<Field
							testId="catalogue-name"
							label={message(language, 'listingName')}
							error={errorFor('name')}
							errorTestId="catalogue-name-error"
							value={name}
							maxLength={80}
							onInput={(v) => edited(() => (name = v))}
						/>
						<Field
							testId="catalogue-area"
							label={message(language, 'listingArea')}
							note={message(language, 'listingAreaNote')}
							error={errorFor('area')}
							value={area}
							maxLength={120}
							onInput={(v) => edited(() => (area = v))}
						/>
					</div>

					<div class="row">
						<Button
							testId="catalogue-save"
							label={message(language, 'save')}
							size="md"
							disabled={!changed || nameMissing}
							state={saving ? 'pending' : 'idle'}
							pendingLabel={message(language, 'saving')}
							onPress={() => void save()}
						/>
					</div>
				</div>
			</Card>

			<Card
				title={message(language, 'logoTitle')}
				note={message(language, 'logoCardNote')}
				testId="catalogue-logo"
			>
				<div class="body tight">
					<ImageInput
						testId="logo"
						src={logoUrl || undefined}
						alt={message(language, 'logoAlt')}
						emptyLabel={message(language, 'logoEmpty')}
						addLabel={message(language, 'logoAdd')}
						replaceLabel={message(language, 'logoReplace')}
						removeLabel={message(language, 'logoRemove')}
						onPick={(picked) => void pickLogo(picked)}
						onRemove={removeLogo}
						removeDisabled={saved.listed}
						state={uploading ? 'pending' : 'idle'}
						pendingLabel={message(language, 'logoUploading')}
						error={logoProblem ? message(language, logoWords[logoProblem]) : undefined}
						note={message(language, saved.listed ? 'logoLockedNote' : 'logoNote')}
					/>
				</div>
			</Card>
		{:else}
			<Card
				title={message(language, 'cataloguePartShops')}
				note={shopId ? undefined : fill(message(language, 'placesShown'), shownCount, shops.length)}
				testId="catalogue-shops"
			>
				<ul class="shops">
					{#each shops.filter((shop) => !shopId || shop.id === shopId) as shop (shop.id)}
						{@const editing = edit?.id === shop.id}
						<li class="shop" data-testid="catalogue-shop-{shop.id}">
							<div class="shop-head">
								<span class="who">
									<span class="shop-name">{shop.name}</span>
									{#if shop.address.trim()}
										<span class="address">{shop.address}</span>
									{:else}
										<span class="no-address">{message(language, 'placeNoAddress')}</span>
									{/if}
									{#if shop.place}
										<bdi class="place" dir="ltr" data-testid="catalogue-place-{shop.id}"
											>{placeText(shop.place)}</bdi
										>
									{/if}
								</span>
								<span class="state">
									{#if shop.shown}
										<Chip
											tone="ok"
											dot
											text={message(language, 'placeShown')}
											testId="catalogue-shown-{shop.id}"
										/>
									{:else}
										<Chip
											tone="flag"
											dot
											text={message(language, 'placeHidden')}
											testId="catalogue-hidden-{shop.id}"
										/>
										<span class="lacks">{lacks(shop)}</span>
									{/if}
								</span>
								{#if !editing}
									<span class="acts">
										{#if !shop.address.trim()}
											<Button
												testId="catalogue-address-{shop.id}"
												tone="text-quiet"
												size="sm"
												label={message(language, 'placeAddressInShops')}
												onPress={() => onShopAddress(shop.id)}
											/>
										{/if}
										<Button
											testId="catalogue-edit-{shop.id}"
											tone="text"
											size="sm"
											label={message(language, shop.place ? 'placeEdit' : 'placeAdd')}
											disabled={edit !== null}
											onPress={() => startEdit(shop)}
										/>
									</span>
								{/if}
							</div>
							{#if editing && edit}
								<MapPin
									testId="catalogue-map"
									lat={both ? both.lat / 1e6 : null}
									lng={both ? both.lng / 1e6 : null}
									onChange={(p) => {
										placeRefused = null;
										if (edit) {
											edit.lat = formatDegrees(Math.round(p.lat * 1e6));
											edit.lng = formatDegrees(Math.round(p.lng * 1e6));
										}
									}}
									label={fill(message(language, 'placeMapLabel'), shop.name)}
									emptyHint={message(language, 'placeMapHint')}
									zoomInLabel={message(language, 'placeZoomIn')}
									zoomOutLabel={message(language, 'placeZoomOut')}
									disabled={placing}
								/>
								<div class="degrees">
									<Field
										testId="catalogue-lat"
										label={message(language, 'placeLatitude')}
										mono
										inputMode="decimal"
										placeholder="30.044420"
										value={edit.lat}
										maxLength={14}
										error={degreesError(lat, 'placeLatitudeRange', 'latitude')}
										errorTestId="catalogue-lat-error"
										onInput={(v) => {
											placeRefused = null;
											if (edit) edit.lat = v;
										}}
										onBlur={() => {
											if (edit) edit.touched = true;
										}}
									/>
									<Field
										testId="catalogue-lng"
										label={message(language, 'placeLongitude')}
										mono
										inputMode="decimal"
										placeholder="31.235712"
										value={edit.lng}
										maxLength={15}
										error={degreesError(lng, 'placeLongitudeRange', 'longitude')}
										errorTestId="catalogue-lng-error"
										onInput={(v) => {
											placeRefused = null;
											if (edit) edit.lng = v;
										}}
										onBlur={() => {
											if (edit) edit.touched = true;
										}}
									/>
								</div>
								<p class="help">{message(language, 'placeHelp')}</p>
								<div class="edit-acts">
									{#if shop.place}
										<Button
											testId="catalogue-remove-place"
											tone="text-quiet"
											size="sm"
											label={message(language, 'placeRemove')}
											disabled={placing}
											onPress={() => removePlace(shop)}
										/>
									{/if}
									<span class="push"></span>
									<Button
										testId="catalogue-cancel-place"
										tone="text-quiet"
										size="sm"
										label={message(language, 'cancel')}
										disabled={placing}
										onPress={() => (edit = null)}
									/>
									<Button
										testId="catalogue-save-place"
										tone="primary"
										size="sm"
										label={message(language, 'placeSave')}
										disabled={!both ||
											placing ||
											(shop.place?.latitudeE6 === both.lat && shop.place?.longitudeE6 === both.lng)}
										state={placing ? 'pending' : 'idle'}
										pendingLabel={message(language, 'saving')}
										onPress={() => void savePlace(shop)}
									/>
								</div>
							{/if}
						</li>
					{/each}
				</ul>
			</Card>
			<p class="explain">{message(language, 'placesFoot')}</p>
		{/if}
	{/if}
</div>

<style>
	.catalogue {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.status {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.line {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-7);
	}

	.url {
		min-inline-size: 0;
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		overflow-wrap: anywhere;
	}

	.push {
		flex: 1;
	}

	.say {
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.missing {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		padding-block-start: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.missing-title {
		font-size: var(--text-sm);
		font-weight: 700;
		color: var(--text-strong);
	}

	.need {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: var(--space-1) var(--space-5);
	}

	.why {
		flex: 1 1 18em;
		min-inline-size: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.parts,
	.segmented,
	.row {
		display: flex;
	}

	.body {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.body.tight {
		gap: var(--space-5);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
		gap: var(--space-7);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.explain,
	.help {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.group .explain {
		color: var(--text-body);
	}

	.shops {
		margin: 0;
		padding: 0;
		list-style: none;
		display: flex;
		flex-direction: column;
	}

	.shop {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7) var(--card-pad);
	}

	.shop + .shop {
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.shop-head {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-7);
	}

	.who {
		flex: 1 1 16em;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.shop-name {
		font-size: var(--text-base);
		font-weight: 700;
		color: var(--text-strong);
	}

	.address {
		font-size: var(--text-sm);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.no-address {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.place {
		font-family: var(--font-mono);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.state {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-1);
	}

	.lacks {
		font-size: var(--text-2xs);
		color: var(--text-body);
	}

	.acts {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	.degrees {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
		gap: var(--space-5);
	}

	.edit-acts {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3);
	}
</style>
